import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Eye, EyeOff, Loader2, ShieldCheck, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { getBackendUrl } from '@/config';

/**
 * SSN Reveal Button Component
 * Allows authorized users (agents/admins) to view full SSN with audit logging
 */
export function SSNRevealButton({ 
  ssnToken, 
  maskedSSN = 'XXX-XX-XXXX',
  label = 'SSN',
  className = ''
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [revealedSSN, setRevealedSSN] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleReveal = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for accessing the SSN');
      return;
    }

    if (!ssnToken) {
      toast.error('No SSN token available');
      return;
    }

    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${backendUrl}/api/ssn/full/${ssnToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason: reason.trim() })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to reveal SSN');
      }

      const data = await response.json();
      setRevealedSSN(data.formatted_ssn || data.ssn);
      toast.success('SSN revealed - access has been logged');
    } catch (error) {
      console.error('Error revealing SSN:', error);
      toast.error(error.message || 'Failed to reveal SSN');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (revealedSSN) {
      navigator.clipboard.writeText(revealedSSN.replace(/-/g, ''));
      setCopied(true);
      toast.success('SSN copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setRevealedSSN(null);
    setReason('');
    setCopied(false);
  };

  // If no token, just show the masked value
  if (!ssnToken) {
    return (
      <div className={className}>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium">{maskedSSN || 'N/A'}</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium">{revealedSSN || maskedSSN}</p>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2"
          onClick={() => setIsDialogOpen(true)}
          title="Reveal full SSN"
        >
          {revealedSSN ? (
            <EyeOff className="h-3 w-3" />
          ) : (
            <Eye className="h-3 w-3" />
          )}
        </Button>
        {revealedSSN && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2"
            onClick={handleCopy}
            title="Copy SSN"
          >
            {copied ? (
              <Check className="h-3 w-3 text-green-600" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Reveal SSN
            </DialogTitle>
            <DialogDescription>
              This action will be logged for SOC 2 compliance. Please provide a valid business reason.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {revealedSSN ? (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-sm text-muted-foreground mb-1">Full SSN:</p>
                <div className="flex items-center gap-3">
                  <p className="text-2xl font-mono font-bold tracking-wider">
                    {revealedSSN}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                  >
                    {copied ? (
                      <Check className="h-4 w-4 mr-1 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4 mr-1" />
                    )}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  This SSN will be hidden when you close this dialog.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label htmlFor="reason">Reason for access *</Label>
                  <Input
                    id="reason"
                    placeholder="e.g., Preparing tax return for client"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Your access will be recorded with timestamp, reason, and IP address.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            {revealedSSN ? (
              <Button onClick={handleClose}>
                Close & Hide SSN
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleReveal} 
                  disabled={loading || !reason.trim()}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Revealing...
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Reveal SSN
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
